import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/App';
import { toast } from 'sonner';
import { format, isPast, parseISO } from 'date-fns';
import { Calendar, Users, TrendingUp, CheckCircle, AlertCircle, Euro, Gavel } from 'lucide-react';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si la prochaine AG est à venir (pas passée)
  const prochaineAgValide = stats?.prochaine_ag && !isPast(parseISO(stats.prochaine_ag.date_ag));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">
          Bienvenue, {user.prenom}
        </h1>
        <p className="text-slate-500 text-lg">
          Copropriété <span className="font-semibold text-slate-700">{stats?.nom_copropriete || ''}</span>
        </p>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Statut financier - Premier élément important */}
        <div 
          className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 card-hover" 
          data-testid="financial-status-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 ${stats?.finances?.is_a_jour ? 'bg-emerald-100' : 'bg-red-100'} rounded-lg flex items-center justify-center`}>
              {stats?.finances?.is_a_jour ? (
                <CheckCircle className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" strokeWidth={1.5} />
              )}
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Mon solde</h3>
          </div>
          <div className="flex items-center gap-3">
            {/* Indicateur rond */}
            <div 
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                stats?.finances?.is_a_jour 
                  ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 ring-4 ring-emerald-100' 
                  : 'bg-gradient-to-br from-red-400 to-red-600 ring-4 ring-red-100'
              }`}
            >
              {stats?.finances?.is_a_jour ? (
                <CheckCircle className="w-6 h-6 text-white" />
              ) : (
                <Euro className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              {stats?.finances?.is_a_jour ? (
                <>
                  <p className="text-xl font-bold text-emerald-600">À jour</p>
                  <p className="text-xs text-slate-500">Tous paiements réglés</p>
                </>
              ) : (
                <>
                  <p className="text-xl font-bold text-red-600">{Math.abs(stats?.finances?.solde || 0).toFixed(2)} €</p>
                  <p className="text-xs text-slate-500">Reste à payer</p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Prochaine AG - Cliquable si existe et à venir */}
        {prochaineAgValide ? (
          <div 
            className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 card-hover cursor-pointer transition-all hover:ring-2 hover:ring-purple-500/50" 
            data-testid="next-ag-card"
            onClick={() => navigate('/assemblees')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/assemblees')}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Gavel className="w-5 h-5 text-purple-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Prochaine AG</h3>
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900 mb-1 truncate">
                {stats.prochaine_ag.titre}
              </p>
              <p className="text-sm text-slate-500">
                {format(parseISO(stats.prochaine_ag.date_ag), 'dd/MM/yyyy')}
              </p>
              <p className="text-xs text-purple-600 mt-2">Cliquez pour consulter</p>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 card-hover" data-testid="next-ag-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Prochaine AG</h3>
            </div>
            <p className="text-slate-500 text-sm">Aucune assemblée prévue</p>
          </div>
        )}

        {/* Copropriétaires - Cliquable */}
        <div 
          className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 card-hover cursor-pointer transition-all hover:ring-2 hover:ring-amber-500/50" 
          data-testid="coproprietaires-card"
          onClick={() => navigate('/coproprietaires')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/coproprietaires')}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Copropriétaires</h3>
          </div>
          <p className="text-4xl font-bold text-slate-900">{stats?.nb_coproprietaires || 0}</p>
          <p className="text-sm text-slate-500 mt-2">Cliquez pour voir la liste</p>
        </div>

        {/* Assemblées - Redirige vers calendrier */}
        <div 
          className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 card-hover cursor-pointer transition-all hover:ring-2 hover:ring-blue-500/50" 
          data-testid="assemblees-card"
          onClick={() => navigate('/calendrier')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/calendrier')}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Assemblées</h3>
          </div>
          <p className="text-4xl font-bold text-slate-900">{stats?.nb_assemblees || 0}</p>
          <p className="text-sm text-slate-500 mt-2">Voir le calendrier</p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
